/**
 * OnboardingFlow - Multi-step onboarding wizard for new users.
 * w2_05: redrawn in the Worthly 2.0 look. w2_07: a fuller intro and money
 * tips page, a clearer step 10, and no pop-up when you finish.
 * The steps, their order, what's
 * saved and the push permission prompt are the same as before.
 */
import { useState } from "react";
import { CURRENCIES } from "../../utils/formatters";
import { keychainService } from "../../services/keychainService";
import {
  W2, W2Page, W2Bubble, W2SwitchRow,
  w2Title, w2Sub, w2Card, w2Lab, w2Field, w2Btn, w2BackBtn, w2Dashed, w2Count,
} from "./w2ui";

// Dismiss keyboard on Enter key
const dismissOnEnter = (e) => {
  if (e.key === 'Enter') {
    e.target.blur();
  }
};

// Soft versions of the card colours, just for picking. The saved value
// ('grey', 'red', ...) is the same as before.
const CARD_COLOURS = [
  { value: 'grey', label: 'Grey', bg: '#DCDBE3' },
  { value: 'red', label: 'Red', bg: '#F5C9C4' },
  { value: 'blue', label: 'Blue', bg: '#BCD3F3' },
  { value: 'orange', label: 'Orange', bg: '#F8D5BC' },
  { value: 'green', label: 'Green', bg: '#BFE6CF' },
  { value: 'pink', label: 'Pink', bg: '#F6C5D8' },
  { value: 'purple', label: 'Purple', bg: '#D3C6F5' },
];

const STEPS = {
  1: { title: 'Your currency', sub: 'You can change it later in Settings.', e: '\u{1F30D}', bg: W2.blue },
  2: { title: 'Monthly money tips', sub: 'Practical tips to help you save more and spend smarter, once a month!', e: '\u{1F48C}', bg: W2.pink },
  3: { title: 'Notifications', sub: "We'll only send what you turn on here. You can change these any time in Settings.", e: '\u{1F514}', bg: W2.butter },
  4: { title: 'Budget categories', sub: 'Where your money goes each month. Add up to 30!', e: '\u{1F3F7}\u{FE0F}', bg: W2.pink },
  5: { title: 'Your cards', sub: 'Up to 3 cards you spend with. Just a nickname!', e: '\u{1F4B3}', bg: W2.lil },
  6: { title: 'Sinking funds', sub: "Up to 8 things you're saving for, like a trip or a new phone.", e: '\u{1F437}', bg: W2.mint },
  7: { title: 'Investments', sub: 'Up to 5 accounts, like an RRSP, TFSA or a brokerage account.', e: '\u{1F4C8}', bg: W2.blue },
  8: { title: 'Assets', sub: 'Up to 5 things you own, like a home or a car.', e: '\u{1F3E1}', bg: W2.butter },
  9: { title: 'Debts', sub: 'Up to 5 debts, like a car loan or a credit card balance.', e: '\u{1F3E6}', bg: W2.pink },
  10: { title: 'Leave out of budget % used tracking', sub: '', e: '\u{1F4CA}', bg: W2.mint },
};

const RemoveBtn = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label="Remove"
    className="flex items-center justify-center flex-shrink-0"
    style={{ width: 40, height: 46, border: 0, background: 'none', color: W2.faint, fontSize: 20, padding: 0, cursor: 'pointer' }}
  >
    {'×'}
  </button>
);

const Seg = ({ value, onChange }) => (
  <div className="flex" style={{ background: W2.field, borderRadius: 999, padding: 3, marginTop: 10 }}>
    {[['debit', 'Debit'], ['credit', 'Credit']].map(([k, l]) => (
      <button
        key={k}
        type="button"
        onClick={() => onChange(k)}
        style={{ flex: 1, border: 0, borderRadius: 999, padding: 8, fontSize: 13, fontWeight: value === k ? 600 : 500, color: value === k ? W2.ink : W2.soft, background: value === k ? '#FFFFFF' : 'transparent', cursor: 'pointer' }}
      >
        {l}
      </button>
    ))}
  </div>
);

const ColourDots = ({ value, onChange }) => (
  <div className="flex flex-wrap" style={{ gap: 8, marginTop: 12 }}>
    {CARD_COLOURS.map((c) => (
      <button
        key={c.value}
        type="button"
        onClick={() => onChange(c.value)}
        aria-label={c.label}
        style={{ width: 30, height: 30, borderRadius: 999, border: 0, padding: 0, background: c.bg, boxShadow: value === c.value ? `0 0 0 2px #FFFFFF, 0 0 0 4px ${W2.ink}` : 'none', cursor: 'pointer' }}
      />
    ))}
  </div>
);

const OnboardingFlow = ({ saveUserData, onComplete, userName }) => {
  const [showIntro, setShowIntro] = useState(true);
  const [step, setStep] = useState(1);
  const totalSteps = 10;

  // Step 1: Budget Categories (up to 30)
  const [categories, setCategories] = useState(['']);

  // Step 0: Currency selection
  const [selectedCurrency, setSelectedCurrency] = useState('CAD');

  // Canadian credit score tracking
  const [creditTrackingEnabled, setCreditTrackingEnabled] = useState(true);

  // Canadian RRSP / TFSA contributions planner
  const [contributionsEnabled, setContributionsEnabled] = useState(true);

  // Newsletter opt-in (default on)
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);

  // Notification preferences (all default on, mirrors Settings keys)
  const [pushEnabled, setPushEnabled] = useState(true);
  const [dailyTransactions, setDailyTransactions] = useState(true);
  const [dailyNospend, setDailyNospend] = useState(true);
  const [remindersEnabled, setRemindersEnabled] = useState(true);

  // Derived currency symbol from selection
  const currencySymbol = CURRENCIES.find(c => c.code === selectedCurrency)?.symbol || '$';

  // Step 2: Cards (up to 3) with colors and type
  const [cards, setCards] = useState([{ name: '', color: 'grey', type: 'debit' }]);
  // Cash payment method (opt-in)
  const [cashEnabled, setCashEnabled] = useState(false);
  const [cashColor, setCashColor] = useState('green');

  // Step 3: Sinking Funds (up to 8) with emojis and starting balance
  const [sinkingFunds, setSinkingFunds] = useState([{ name: '', emoji: '💰', startingBalance: '' }]);
  const fundEmojis = ['💰', '💵', '🏠', '✈️', '🚗', '🚙', '🎁', '💍', '🎓', '🏥', '📱', '🛋️', '🎄', '👶', '📝', '🚨', '❤️'];

  // Step 4: Investment Accounts (up to 5) with starting balance
  const [investmentAccounts, setInvestmentAccounts] = useState([{ name: '', startingBalance: '' }]);

  // Step 5: Assets (up to 5) with current value
  const [assets, setAssets] = useState([{ name: '', currentValue: '' }]);

  // Step 6: Debts (up to 5) with starting and current balance
  const [debts, setDebts] = useState([{ name: '', originalAmount: '', currentBalance: '' }]);

  // Step 7: Excluded Categories
  const [excludedCategories, setExcludedCategories] = useState([]);

  const addItem = (setter, items, max) => {
    if (items.length < max) {
      setter([...items, '']);
    }
  };

  const addCardItem = () => {
    if (cards.length < 3) {
      setCards([...cards, { name: '', color: 'pink', type: 'debit' }]);
    }
  };

  const addFundItem = () => {
    if (sinkingFunds.length < 8) {
      setSinkingFunds([...sinkingFunds, { name: '', emoji: '💰', startingBalance: '' }]);
    }
  };

  const addInvestmentItem = () => {
    if (investmentAccounts.length < 5) {
      setInvestmentAccounts([...investmentAccounts, { name: '', startingBalance: '' }]);
    }
  };

  const updateInvestmentItem = (index, field, value) => {
    const newInvestments = [...investmentAccounts];
    newInvestments[index] = { ...newInvestments[index], [field]: value };
    setInvestmentAccounts(newInvestments);
  };

  const addAssetItem = () => {
    if (assets.length < 5) {
      setAssets([...assets, { name: '', currentValue: '' }]);
    }
  };

  const updateAssetItem = (index, field, value) => {
    const newAssets = [...assets];
    newAssets[index] = { ...newAssets[index], [field]: value };
    setAssets(newAssets);
  };

  const addDebtItem = () => {
    if (debts.length < 5) {
      setDebts([...debts, { name: '', originalAmount: '', currentBalance: '' }]);
    }
  };

  const updateDebtItem = (index, field, value) => {
    const newDebts = [...debts];
    newDebts[index] = { ...newDebts[index], [field]: value };
    setDebts(newDebts);
  };

  const removeItem = (setter, items, index) => {
    if (items.length > 1) {
      setter(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (setter, items, index, value) => {
    const newItems = [...items];
    newItems[index] = value;
    setter(newItems);
  };

  const updateCardItem = (index, field, value) => {
    const newCards = [...cards];
    newCards[index] = { ...newCards[index], [field]: value };
    setCards(newCards);
  };

  const updateFundItem = (index, field, value) => {
    const newFunds = [...sinkingFunds];
    newFunds[index] = { ...newFunds[index], [field]: value };
    setSinkingFunds(newFunds);
  };

  const getValidItems = (items) => items.filter(item => {
    if (typeof item === 'string') return item.trim() !== '';
    if (typeof item === 'object' && item.name) return item.name.trim() !== '';
    return false;
  });

  const handleNext = async () => {
    // When leaving the notifications step, fire iOS permission prompt if master is on
    if (step === 3 && pushEnabled) {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform()) {
          const { PushNotifications } = await import('@capacitor/push-notifications');
          const permResult = await PushNotifications.requestPermissions();
          if (permResult.receive === 'granted') {
            await PushNotifications.register();
          } else {
            // User denied at the OS level — reflect that so Settings doesn't show it as on
            setPushEnabled(false);
            setDailyTransactions(false);
            setDailyNospend(false);
          }
        }
      } catch (e) {
        console.error('Push permission request from onboarding step 3:', e);
      }
    }
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleComplete = async () => {
    const validCategories = getValidItems(categories);
    const validCards = getValidItems(cards);
    const validSinkingFunds = getValidItems(sinkingFunds);
    const validInvestmentAccounts = getValidItems(investmentAccounts);
    const validAssets = getValidItems(assets);
    const validDebts = getValidItems(debts);

    // Get current month key for starting balances
    const now = new Date();
    const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    // Create sinking funds with proper structure (now includes user-selected emoji and starting balance)
    const sinkingFundsData = validSinkingFunds.map((fund, i) => ({
      id: `fund_${Date.now()}_${i}`,
      name: fund.name,
      emoji: fund.emoji || '💰',
      goal: 0,
      starting_balance: fund.startingBalance ? parseFloat(fund.startingBalance) || 0 : 0,
      current_by_month: {}
    }));

    // Create investment accounts with proper structure (now includes starting balance)
    const investmentAccountsData = validInvestmentAccounts.map((inv, i) => ({
      id: `inv_${Date.now()}_${i}`,
      name: inv.name,
      goal: 0,
      starting_balance: inv.startingBalance ? parseFloat(inv.startingBalance) || 0 : 0,
      current_by_month: {}
    }));

    // Create assets with proper structure (uses starting_balance)
    const assetsData = validAssets.map((asset, i) => ({
      id: `asset_${Date.now()}_${i}`,
      name: asset.name,
      goal: 0,
      starting_balance: parseFloat(asset.currentValue) || 0,
      current_by_month: {}
    }));

    // Create debts with proper structure (now includes original and current balance)
    const debtsData = validDebts.map((debt, i) => ({
      id: `debt_${Date.now()}_${i}`,
      name: debt.name,
      original_amount: parseFloat(debt.originalAmount) || 0,
      current_by_month: debt.currentBalance ? { [currentMonthKey]: parseFloat(debt.currentBalance) || 0 } : {}
    }));

    // Convert cards to objects with user-selected color and type
    const cardsData = validCards.map(card => ({
      name: card.name,
      color: card.color || 'pink',
      type: card.type || 'debit'
    }));

    // Save all data and wait for it to complete
    await saveUserData({
      currency: selectedCurrency,
      credit_score: { enabled: creditTrackingEnabled, goal: 750 },
      plans: { contributionsEnabled: contributionsEnabled, contributionsSeeded: true },
      budget_settings: {
        monthly_budget: 0,
        total_money: 0,
        categories: validCategories,
        excluded_categories: excludedCategories,
        cash_enabled: cashEnabled,
        cash_color: cashColor
      },
      cards: cardsData,
      sinking_funds: sinkingFundsData,
      investment_accounts: investmentAccountsData,
      assets: assetsData,
      debts: debtsData,
      newsletter_opted_in: newsletterOptIn,
      notification_preferences: {
        push_enabled: pushEnabled,
        daily_transactions: pushEnabled && dailyTransactions,
        daily_nospend: pushEnabled && dailyNospend,
        reminders: pushEnabled && remindersEnabled,
      },
      onboarding_complete: true,
      tutorial_completed: false  // Will trigger tutorial to show
    });

    // Subscribe to newsletter if opted in
    if (newsletterOptIn) {
      try {

        await fetch(`${(process.env.REACT_APP_BACKEND_URL || 'https://worthly-backend-production.up.railway.app')}/api/newsletter/subscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${keychainService.getCached()}`
          }
        });
      } catch (err) {
        console.log('Newsletter subscribe error:', err);
        // Don't block onboarding completion
      }
    }

    onComplete();
  };

  // Quick add categories with emojis as part of the text
  const quickAddCategories = ['🛒 Groceries', '🏠 Housing', '🚗 Transport', '🍽️ Dining', '💊 Health', '👗 Shopping'];

  const isQuickAddSelected = (label) => {
    return categories.some(c => c.trim().toLowerCase() === label.trim().toLowerCase());
  };

  // Tap a quick-add chip to add it, tap it again to take it off.
  const toggleQuickAdd = (label) => {
    if (isQuickAddSelected(label)) {
      const left = categories.filter(c => c.trim().toLowerCase() !== label.trim().toLowerCase());
      setCategories(left.length ? left : ['']);
      return;
    }
    // Find first empty slot or add new
    const emptyIndex = categories.findIndex(c => c.trim() === '');
    if (emptyIndex !== -1) {
      const newCategories = [...categories];
      newCategories[emptyIndex] = label;
      setCategories(newCategories);
    } else if (categories.length < 30) {
      setCategories([...categories, label]);
    }
  };

  const toggleExcluded = (key) => {
    setExcludedCategories(excludedCategories.includes(key) ? excludedCategories.filter(c => c !== key) : [...excludedCategories, key]);
  };

  const amountField = (value, onChange, width = 104) => (
    <input
      type="number"
      inputMode="decimal"
      value={value}
      onChange={onChange}
      placeholder={`${currencySymbol}0`}
      onKeyDown={dismissOnEnter}
      style={{ ...w2Field, width, flex: 'none', background: W2.field }}
    />
  );

  const getCurrentStepContent = () => {
    switch (step) {
      case 1:
        return (
          <>
            <div style={{ maxHeight: '40vh', overflowY: 'auto', marginTop: 12, padding: 2 }}>
              {CURRENCIES.map(cur => {
                const isSelected = selectedCurrency === cur.code;
                return (
                  <button
                    key={cur.code}
                    type="button"
                    onClick={() => {
                      setSelectedCurrency(cur.code);
                      setCreditTrackingEnabled(cur.code === 'CAD');
                      setContributionsEnabled(cur.code === 'CAD');
                    }}
                    data-testid={`onboard-currency-${cur.code}`}
                    className="w-full flex items-center text-left"
                    style={{ gap: 12, padding: '12px 14px', borderRadius: 18, border: 0, background: '#FFFFFF', marginTop: 8, boxShadow: isSelected ? `inset 0 0 0 2px ${W2.lilBar}` : 'none', cursor: 'pointer' }}
                  >
                    <span style={{ fontSize: 22 }}>{cur.flag}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: W2.ink }}>{cur.name}</span>
                      <span style={{ display: 'block', fontSize: 12, color: W2.soft, marginTop: 1 }}>{cur.subtitle} {'·'} {cur.symbol}</span>
                    </span>
                    {isSelected && (
                      <span className="flex items-center justify-center flex-shrink-0" style={{ width: 22, height: 22, borderRadius: 999, background: W2.lilDot, color: '#FFFFFF', fontSize: 12 }}>{'✓'}</span>
                    )}
                  </button>
                );
              })}
            </div>
            <span style={w2Lab}>FOR CANADA</span>
            <div style={{ ...w2Card, padding: '4px 16px' }}>
              <W2SwitchRow first title="Track your credit score" sub="Once a month, on Home" on={creditTrackingEnabled} onToggle={() => setCreditTrackingEnabled(!creditTrackingEnabled)} testId="onboard-credit-tracking-toggle" />
              <W2SwitchRow title="RRSP and TFSA planner" sub="Set targets for your contributions" on={contributionsEnabled} onToggle={() => setContributionsEnabled(!contributionsEnabled)} testId="onboard-contributions-toggle" />
            </div>
          </>
        );
      case 2:
        return (
          <>
            <div style={{ ...w2Card, padding: '4px 16px', marginTop: 20 }}>
              <W2SwitchRow first emoji={'\u{1F48C}'} title="Monthly Money Memo" sub="Tips, insights and updates to your inbox" on={newsletterOptIn} onToggle={() => setNewsletterOptIn(!newsletterOptIn)} testId="onboard-newsletter-toggle" />
            </div>
            <span style={{ ...w2Lab, marginTop: 22 }}>WHAT YOU'LL GET</span>
            {(() => {
              const nextMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1);
              const line = (e, bg, text) => (
                <div className="flex items-start" style={{ gap: 10, marginTop: 10 }}>
                  <span className="flex items-center justify-center flex-shrink-0" style={{ width: 28, height: 28, borderRadius: 10, background: bg, fontSize: 13 }}>{e}</span>
                  <span style={{ fontSize: 13, lineHeight: 1.45, color: '#5A5664', paddingTop: 4 }}>{text}</span>
                </div>
              );
              return (
                <div style={{ ...w2Card, padding: 16, boxShadow: '0 6px 20px rgba(80,60,130,0.06)' }}>
                  <div className="flex justify-between" style={{ fontSize: 11.5, color: W2.soft }}>
                    <span>From Worthly</span>
                    <span>{nextMonth.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: W2.ink, marginTop: 6 }}>Your {nextMonth.toLocaleDateString('en-US', { month: 'long' })} Money Memo {'✨'}</div>
                  {line('\u{1F381}', W2.mint, 'A sinking fund idea for the month ahead')}
                  {line('\u{1F4CA}', W2.blue, 'Three numbers worth checking before the month ends')}
                  {line('✨', W2.lil, "What's new in Worthly this month")}
                </div>
              );
            })()}
            <p style={{ ...w2Sub, fontSize: 12.5, margin: '12px 4px 0' }}>You can unsubscribe any time!</p>
          </>
        );
      case 3:
        return (
          <div style={{ ...w2Card, padding: '4px 16px', marginTop: 16 }}>
            {[
              { key: 'push', label: 'Push notifications', subtitle: 'Budgets, transactions, reminders, achievements and updates', icon: '🔔', value: pushEnabled, setter: setPushEnabled },
              { key: 'reminders', label: 'Reminders', subtitle: 'Reminders you set for yourself about your money', icon: '⏰', value: remindersEnabled, setter: setRemindersEnabled },
              { key: 'transactions', label: 'Log transactions', subtitle: 'A daily reminder to log your spending', icon: '✍️', value: dailyTransactions, setter: setDailyTransactions },
              { key: 'nospend', label: 'No-spend tracker', subtitle: 'A daily reminder to mark your no-spend day', icon: '📅', value: dailyNospend, setter: setDailyNospend },
            ].map((item, i) => (
              <W2SwitchRow key={item.key} first={i === 0} emoji={item.icon} title={item.label} sub={item.subtitle} on={item.value} onToggle={() => item.setter(!item.value)} testId={`onboard-notif-${item.key}-toggle`} />
            ))}
          </div>
        );
      case 4:
        return (
          <>
            <span style={w2Lab}>QUICK ADD</span>
            <div className="flex flex-wrap" style={{ gap: 7 }}>
              {quickAddCategories.map((label) => {
                const on = isQuickAddSelected(label);
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => toggleQuickAdd(label)}
                    data-testid={`quick-add-${label.split(' ').pop().toLowerCase()}`}
                    style={{ border: 0, borderRadius: 999, padding: '9px 13px', fontSize: 13.5, fontWeight: on ? 600 : 500, background: on ? W2.lil : '#FFFFFF', color: on ? W2.lilT : W2.ink, cursor: 'pointer' }}
                  >
                    {label}{on ? ' ✓' : ''}
                  </button>
                );
              })}
            </div>
            <span style={w2Lab}>YOUR CATEGORIES</span>
            {categories.map((cat, index) => (
              <div key={index} className="flex items-center" style={{ gap: 4, marginTop: index ? 8 : 0 }}>
                <input
                  value={cat}
                  onChange={(e) => updateItem(setCategories, categories, index, e.target.value)}
                  placeholder="Add a category"
                  onKeyDown={dismissOnEnter}
                  style={w2Field}
                />
                {categories.length > 1 && <RemoveBtn onClick={() => removeItem(setCategories, categories, index)} />}
              </div>
            ))}
            {categories.length < 30 && <button type="button" onClick={() => addItem(setCategories, categories, 30)} style={w2Dashed}>+ Add another</button>}
            <p style={w2Count}>{getValidItems(categories).length} of 30 added</p>
          </>
        );
      case 5:
        return (
          <>
            {cards.map((card, index) => (
              <div key={index} style={{ ...w2Card, padding: 14, marginTop: index ? 10 : 14 }}>
                <div className="flex items-center" style={{ gap: 4 }}>
                  <input
                    value={card.name}
                    onChange={(e) => updateCardItem(index, 'name', e.target.value.slice(0, 20))}
                    placeholder={`Card ${index + 1}, like Scotia Debit`}
                    maxLength={20}
                    onKeyDown={dismissOnEnter}
                    style={{ ...w2Field, background: W2.field }}
                  />
                  {cards.length > 1 && <RemoveBtn onClick={() => removeItem(setCards, cards, index)} />}
                </div>
                <Seg value={card.type || 'debit'} onChange={(v) => updateCardItem(index, 'type', v)} />
                <ColourDots value={card.color} onChange={(v) => updateCardItem(index, 'color', v)} />
              </div>
            ))}
            {cards.length < 3 && <button type="button" onClick={addCardItem} style={w2Dashed}>+ Add another card</button>}
            <div style={{ ...w2Card, padding: '2px 16px', marginTop: 10 }}>
              <W2SwitchRow first emoji={'💵'} title="Use cash too?" sub="Track cash next to your cards" on={cashEnabled} onToggle={() => setCashEnabled(!cashEnabled)} />
              {cashEnabled && (
                <div style={{ paddingBottom: 14 }}>
                  <span style={{ display: 'block', fontSize: 12.5, color: W2.soft }}>A colour for cash</span>
                  <ColourDots value={cashColor} onChange={setCashColor} />
                </div>
              )}
            </div>
            <p style={w2Count}>{getValidItems(cards).length} of 3 added</p>
          </>
        );
      case 6:
        return (
          <>
            {sinkingFunds.map((fund, index) => (
              <div key={index} style={{ ...w2Card, padding: 14, marginTop: index ? 10 : 14 }}>
                <div className="flex items-center" style={{ gap: 8 }}>
                  <input
                    value={fund.name}
                    onChange={(e) => updateFundItem(index, 'name', e.target.value)}
                    placeholder={`Fund ${index + 1}`}
                    onKeyDown={dismissOnEnter}
                    style={{ ...w2Field, background: W2.field }}
                  />
                  {amountField(fund.startingBalance, (e) => updateFundItem(index, 'startingBalance', e.target.value), 96)}
                  {sinkingFunds.length > 1 && <RemoveBtn onClick={() => removeItem(setSinkingFunds, sinkingFunds, index)} />}
                </div>
                <div className="flex flex-wrap" style={{ gap: 6, marginTop: 10 }}>
                  {fundEmojis.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => updateFundItem(index, 'emoji', emoji)}
                      style={{ width: 36, height: 36, borderRadius: 12, border: 0, padding: 0, fontSize: 17, background: fund.emoji === emoji ? W2.lil : W2.field, boxShadow: fund.emoji === emoji ? `inset 0 0 0 2px ${W2.lilBar}` : 'none', cursor: 'pointer' }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {sinkingFunds.length < 8 && <button type="button" onClick={addFundItem} style={w2Dashed}>+ Add another fund</button>}
            <p style={w2Count}>{getValidItems(sinkingFunds).length} of 8 added</p>
          </>
        );
      case 7:
        return (
          <>
            {investmentAccounts.map((inv, index) => (
              <div key={index} className="flex items-center" style={{ gap: 8, marginTop: index ? 8 : 14 }}>
                <input
                  value={inv.name}
                  onChange={(e) => updateInvestmentItem(index, 'name', e.target.value)}
                  placeholder={`Account ${index + 1}`}
                  onKeyDown={dismissOnEnter}
                  style={w2Field}
                />
                {amountField(inv.startingBalance, (e) => updateInvestmentItem(index, 'startingBalance', e.target.value))}
                {investmentAccounts.length > 1 && <RemoveBtn onClick={() => removeItem(setInvestmentAccounts, investmentAccounts, index)} />}
              </div>
            ))}
            {investmentAccounts.length < 5 && <button type="button" onClick={addInvestmentItem} style={w2Dashed}>+ Add another account</button>}
            <p style={w2Count}>{getValidItems(investmentAccounts).length} of 5 added</p>
          </>
        );
      case 8:
        return (
          <>
            {assets.map((asset, index) => (
              <div key={index} className="flex items-center" style={{ gap: 8, marginTop: index ? 8 : 14 }}>
                <input
                  value={asset.name}
                  onChange={(e) => updateAssetItem(index, 'name', e.target.value)}
                  placeholder={`Asset ${index + 1}, like Home or Car`}
                  onKeyDown={dismissOnEnter}
                  style={w2Field}
                />
                {amountField(asset.currentValue, (e) => updateAssetItem(index, 'currentValue', e.target.value), 112)}
                {assets.length > 1 && <RemoveBtn onClick={() => removeItem(setAssets, assets, index)} />}
              </div>
            ))}
            {assets.length < 5 && <button type="button" onClick={addAssetItem} style={w2Dashed}>+ Add another asset</button>}
            <p style={w2Count}>{getValidItems(assets).length} of 5 added</p>
          </>
        );
      case 9:
        return (
          <>
            {debts.map((debt, index) => (
              <div key={index} style={{ ...w2Card, padding: 14, marginTop: index ? 10 : 14 }}>
                <div className="flex items-center" style={{ gap: 4 }}>
                  <input
                    value={debt.name}
                    onChange={(e) => updateDebtItem(index, 'name', e.target.value)}
                    placeholder={`Debt ${index + 1}, like Car loan`}
                    onKeyDown={dismissOnEnter}
                    style={{ ...w2Field, background: W2.field }}
                  />
                  {debts.length > 1 && <RemoveBtn onClick={() => removeItem(setDebts, debts, index)} />}
                </div>
                <div className="grid grid-cols-2" style={{ gap: 8, marginTop: 10 }}>
                  <label style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 12, color: W2.soft, margin: '0 4px 5px' }}>Original amount</span>
                    {amountField(debt.originalAmount, (e) => updateDebtItem(index, 'originalAmount', e.target.value), '100%')}
                  </label>
                  <label style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 12, color: W2.soft, margin: '0 4px 5px' }}>Balance now</span>
                    {amountField(debt.currentBalance, (e) => updateDebtItem(index, 'currentBalance', e.target.value), '100%')}
                  </label>
                </div>
              </div>
            ))}
            {debts.length < 5 && <button type="button" onClick={addDebtItem} style={w2Dashed}>+ Add another debt</button>}
            <p style={w2Count}>{getValidItems(debts).length} of 5 added</p>
          </>
        );
      case 10: {
        const validCats = getValidItems(categories);
        const now = new Date();
        const monthGone = Math.max(1, Math.round((now.getDate() / new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()) * 100));
        const barBox = (label, pct, colour) => (
          <div style={{ background: '#FFFFFF', borderRadius: 16, padding: '10px 12px' }}>
            <div className="flex items-baseline justify-between" style={{ gap: 6 }}>
              <span style={{ fontSize: 12, color: W2.soft }}>{label}</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: W2.ink }}>{pct}%</span>
            </div>
            <div style={{ height: 6, borderRadius: 9, background: '#F0EDF5', marginTop: 7, overflow: 'hidden' }}>
              <i style={{ display: 'block', height: '100%', width: pct + '%', background: colour, borderRadius: 9 }} />
            </div>
          </div>
        );
        const check = (key, title) => {
          const on = excludedCategories.includes(key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggleExcluded(key)}
              className="w-full flex items-center text-left"
              style={{ gap: 12, padding: '12px 14px', background: '#FFFFFF', borderRadius: 16, border: 0, marginTop: 7, fontSize: 14.5, fontWeight: 500, color: W2.ink, cursor: 'pointer' }}
            >
              <span className="flex items-center justify-center flex-shrink-0" style={{ width: 22, height: 22, borderRadius: 7, background: on ? W2.lilDot : 'transparent', boxShadow: on ? 'none' : 'inset 0 0 0 2px #DCD3F4', color: '#FFFFFF', fontSize: 12 }}>{on ? '✓' : ''}</span>
              <span style={{ minWidth: 0 }}>{title}</span>
            </button>
          );
        };
        return (
          <>
            <div style={{ background: W2.field, borderRadius: 22, padding: 10, marginTop: 14, boxShadow: `inset 0 0 0 1px ${W2.line}` }}>
              <div className="grid grid-cols-2" style={{ gap: 8 }}>
                {barBox('Budget used', 50, W2.pinkBar)}
                {barBox('Month gone', monthGone, W2.lilBar)}
              </div>
            </div>
            <p style={{ ...w2Sub, fontSize: 13.5, color: '#6F6A7B', margin: '12px 4px 0' }}>
              These two bars show how much of your budget you've spent next to how far through the month you are, so you can see if you're on pace.
            </p>
            <p style={{ ...w2Sub, fontSize: 13.5, color: '#6F6A7B', margin: '8px 4px 6px' }}>
              Big bills throw this off. If rent is {currencySymbol}2,000 of a {currencySymbol}4,000 budget, you'd be at 50% on day 1! Pick anything you'd like to leave out, so the bar only tracks your day-to-day spending.
            </p>
            {check('__DEBT_PAYMENTS__', '\u{1F4B3} Debt payments')}
            {validCats.map((cat) => check(cat, cat))}
            {validCats.length === 0 && <p style={w2Count}>No categories added yet</p>}
            <p style={w2Count}>
              {excludedCategories.length} left out {'·'} you can change this any time in Settings
            </p>
          </>
        );
      }
      default:
        return null;
    }
  };

  // Pre-onboarding intro screen — shown once before the stepped flow begins
  if (showIntro) {
    // Resolve first name from multiple sources so it works even if caller forgot to pass userName
    const resolveName = () => {
      if (userName && typeof userName === 'string') return userName;
      try {
        const cached = localStorage.getItem('userData');
        if (cached) {
          const parsed = JSON.parse(cached);
          return parsed?.name || parsed?.first_name || parsed?.firstName || '';
        }
      } catch (e) { /* ignore */ }
      return '';
    };
    const firstName = (resolveName() || '').trim().split(' ')[0] || 'there';
    return (
      <W2Page
        testId="onboard-intro"
        foot={<button type="button" onClick={() => setShowIntro(false)} style={w2Btn} data-testid="onboard-intro-continue">Let's get started</button>}
      >
        <h1 style={{ ...w2Title, fontSize: 30, marginTop: 18 }}>Hi, {firstName}! {'✨'}</h1>
        <p style={{ ...w2Sub, fontSize: 15 }}>Let's make Worthly yours! Complete these quick steps to help Worthly match how you spend, save and plan.</p>
        <div className="flex items-center" style={{ gap: 10, marginTop: 20 }}>
          <div className="grid" style={{ flex: 1, gridTemplateColumns: 'repeat(10, 1fr)', gap: 4 }}>
            {[W2.blueBar, W2.blueBar, W2.blueBar, W2.pinkBar, W2.pinkBar, W2.mintBar, W2.mintBar, W2.lilBar, W2.lilBar, W2.pinkBar].map((c, i) => (
              <i key={i} style={{ display: 'block', height: 6, borderRadius: 9, background: c }} />
            ))}
          </div>
          <span style={{ fontSize: 12, color: W2.soft, fontWeight: 500 }}>10 quick steps</span>
        </div>
        <div style={{ ...w2Card, padding: '4px 16px', marginTop: 14 }}>
          {[
            ['\u{1F30D}', W2.blue, 'Currency and reminders', 'Your currency, money tips and notifications'],
            ['\u{1F3F7}\u{FE0F}', W2.pink, 'Your budget', 'Spending categories and the cards you use'],
            ['\u{1F437}', W2.mint, 'Your savings', 'Sinking funds and investments'],
            ['\u{1F3E1}', W2.lil, 'What you own and owe', 'Assets and debts, for your net worth'],
          ].map(([e, bg, t, x], i) => (
            <div key={t} className="flex items-center" style={{ gap: 14, padding: '14px 0', borderTop: i ? `1px solid ${W2.line}` : 0 }}>
              <span className="flex items-center justify-center flex-shrink-0" style={{ width: 46, height: 46, borderRadius: 16, background: bg, fontSize: 21 }}>{e}</span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: W2.ink }}>{t}</span>
                <span style={{ display: 'block', fontSize: 13, color: W2.soft, lineHeight: 1.45, marginTop: 2 }}>{x}</span>
              </span>
            </div>
          ))}
        </div>
        <div style={{ background: W2.butter, color: '#5E5132', borderRadius: 18, padding: '13px 15px', fontSize: 13.5, lineHeight: 1.5, marginTop: 12 }}>
          {'⏭️'} You can skip anything you like and add it later in Settings!
        </div>
      </W2Page>
    );
  }

  const meta = STEPS[step];
  const last = step === totalSteps;
  return (
    <W2Page
      testId={`onboard-step-${step}`}
      foot={
        <>
          <div className="flex" style={{ gap: 10 }}>
            {step > 1 && <button type="button" onClick={handleBack} style={w2BackBtn} data-testid="onboarding-back-btn">Back</button>}
            <button type="button" onClick={handleNext} style={{ ...w2Btn, flex: 1, width: 'auto' }} data-testid="onboarding-next-btn">
              {last ? 'Finish setup ✓' : 'Continue'}
            </button>
          </div>
          {!last && (
            <button type="button" onClick={handleNext} style={{ display: 'block', width: '100%', border: 0, background: 'none', padding: '10px 0 0', fontSize: 13, color: W2.soft, cursor: 'pointer' }} data-testid="onboarding-skip-btn">
              Skip for now
            </button>
          )}
        </>
      }
    >
      <div className="flex items-center" style={{ gap: 10, marginBottom: 16 }}>
        <div style={{ flex: 1, height: 6, borderRadius: 9, background: '#E5E0F0', overflow: 'hidden' }}>
          <i style={{ display: 'block', height: '100%', width: `${(step / totalSteps) * 100}%`, borderRadius: 9, background: W2.lilBar, transition: 'width .3s' }} />
        </div>
        <span style={{ fontSize: 12, color: W2.soft, fontWeight: 500, whiteSpace: 'nowrap' }}>{step} of {totalSteps}</span>
      </div>
      <div className="flex items-start justify-between" style={{ gap: 12, marginTop: step === 2 || step === 3 ? 10 : 0 }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ ...w2Title, fontSize: step === 10 ? 24 : 26 }}>{meta.title}</h1>
          {meta.sub && <p style={{ ...w2Sub, marginTop: 6 }}>{meta.sub}</p>}
        </div>
        <W2Bubble bg={meta.bg} e={meta.e} />
      </div>
      {getCurrentStepContent()}
    </W2Page>
  );
};

export default OnboardingFlow;
